import type { LookUpCityResponse } from "@freightsimple/generated-dashboard-openapi-client/models/LookUpCityResponse";
import type { SavedCommodity } from "@freightsimple/generated-dashboard-openapi-client/models/SavedCommodity";
import type { SavedLocation } from "@freightsimple/generated-dashboard-openapi-client/models/SavedLocation";
import { ResponseError } from "@freightsimple/generated-dashboard-openapi-client/runtime";
import type { UUID } from "@/Shared/types";
import BookShipmentDetailsScreen from "./BookShipmentDetailsScreen";
import { usePostalCodesApi } from "@/Hooks/usePostalCodesApi";
import { useShipmentDraftsApi } from "@/Hooks/useShipmentDraftsApi";
import type { ShipmentDraftState } from "@/Shared/Types/bookShipmentSliceTypes";
import { loadFromDraft } from "@/Slices/bookShipmentSlice";
import { useDispatch } from "react-redux";
import { useEffect, useState } from "react";
import { CheckCircleIcon } from "@phosphor-icons/react/CheckCircle";
import { WarningIcon } from "@phosphor-icons/react/Warning";
import YellowExclamationAnimatedIcon from "@/Shared/Animations/YellowExclamationAnimatedIcon";
import PageLoadingSpinner from "@/Shared/Components/CustomComponents/PageLoadingSpinner";
import { LoadFailed } from "@/Shared/Components/LoadFailed";
import { Button } from "@freightsimple/ui-core/Controls/Button";
import { Heading } from "@freightsimple/ui-core/Typography/Heading";
import { Icon } from "@freightsimple/ui-core/Icons/Icon";
import { Row } from "@freightsimple/ui-core/ComponentLayout/Row";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import { T } from "@freightsimple/ui-core/Typography/T";
import { useNavigate } from "react-router-dom";
import {
  setRedirectUrl,
  useAuthentication,
} from "@/Slices/authenticationSlice";
interface BookShipmentDraftScreenProps {
  shipmentDraftId: string;
  savedLocations: Array<SavedLocation>;
  savedCommodities: Array<SavedCommodity>;
  defaultPickupLocationId: UUID | undefined;
  defaultDeliveryLocationId: UUID | undefined;
}

export function BookShipmentDraftScreen(props: BookShipmentDraftScreenProps) {
  const {
    shipmentDraftId,
    savedCommodities,
    savedLocations,
    defaultDeliveryLocationId,
    defaultPickupLocationId,
  } = props;

  const [error, setError] = useState<number>();
  // Starts true so the forced-validation form never renders, all red, before
  // the draft has filled it in
  const [loading, setLoading] = useState(true);
  const draftsApi = useShipmentDraftsApi();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const postalCodesApi = usePostalCodesApi();
  const { isAuthenticated } = useAuthentication();

  async function fetchShipmentDraft() {
    setError(undefined);
    setLoading(true);
    try {
      const response = await draftsApi.getShipmentDraftById({
        shipmentDraftId,
      });

      const draft: ShipmentDraftState = {
        ...response,
        createdAt: response.createdAt?.toISOString(),
      };

      const [pickupResponse, deliveryResponse] =
        await getAdjustedLocations(draft);

      if (pickupResponse) {
        draft.pickup = {
          ...draft.pickup,
          ...pickupResponse,
        };

        draft.pickupLatitudeLongitude =
          pickupResponse.longitude !== undefined &&
          pickupResponse.latitude !== undefined
            ? {
                longitude: pickupResponse.longitude,
                latitude: pickupResponse.latitude,
              }
            : undefined;
      }

      if (deliveryResponse) {
        draft.delivery = {
          ...draft.delivery,
          ...deliveryResponse,
        };

        draft.deliveryLatitudeLongitude =
          deliveryResponse.longitude !== undefined &&
          deliveryResponse.latitude !== undefined
            ? {
                longitude: deliveryResponse.longitude,
                latitude: deliveryResponse.latitude,
              }
            : undefined;
      }

      dispatch(loadFromDraft({ draft, savedLocations }));
    } catch (e) {
      if (e instanceof ResponseError) {
        setError(e.response.status);
      } else {
        setError(500);
      }
      console.error(e);
    } finally {
      setLoading(false);
    }
  }

  async function getAdjustedLocations(
    draft: ShipmentDraftState,
  ): Promise<[LookUpCityResponse | null, LookUpCityResponse | null]> {
    const lookupPromises = [
      draft.pickup?.postalCode
        ? postalCodesApi.lookUpCity({ postalCode: draft.pickup.postalCode })
        : Promise.resolve(undefined),

      draft.delivery?.postalCode
        ? postalCodesApi.lookUpCity({ postalCode: draft.delivery.postalCode })
        : Promise.resolve(undefined),
    ];

    const results = await Promise.allSettled(lookupPromises);

    return results.map((result) =>
      result.status === "fulfilled" ? result.value : undefined,
    ) as [LookUpCityResponse | null, LookUpCityResponse | null];
  }

  useEffect(() => {
    if (isAuthenticated) {
      fetchShipmentDraft();
    } else {
      dispatch(
        setRedirectUrl(`/book/draft?shipmentDraftId=${shipmentDraftId}`),
      );
      navigate("/sign-in");
    }
  }, [shipmentDraftId, isAuthenticated]);

  if (loading) {
    return <PageLoadingSpinner />;
  }

  if (error === 401) {
    return (
      <Stack gap={5} align="center">
        <Stack gap={0} align="center">
          {/* Replica of the legacy Cargo Heading0 (32px bold). */}
          <Heading level={1}>Unfortunately, you can't access this page</Heading>
          {/* Replica of the legacy Cargo Microcopy (16px grey text). */}
          <T size="base" color="secondary">
            It looks like you're trying to view a draft, but you need to sign in
            with the account that created it.
          </T>
        </Stack>
        <Button size="link" href="/book/details">
          Get Quotes for a Different Shipment
        </Button>
      </Stack>
    );
  }

  if (error === 404) {
    return (
      <Stack gap={6} align="center">
        <Stack gap={0} align="center">
          <YellowExclamationAnimatedIcon size={80} />
          {/* Replica of the legacy Cargo Heading0 (32px bold). */}
          <Heading level={1} align="center">
            Draft Not Found
          </Heading>
          {/* Replica of the legacy Cargo Microcopy (16px grey text). */}
          <T size="base" color="secondary" align="center">
            We couldn't find a draft with this ID. This usually happens when:
          </T>
        </Stack>

        <Stack gap={5} align="start">
          <Row gap={3}>
            <Icon
              icon={CheckCircleIcon}
              size="xl"
              weight="fill"
              color="text-green"
            />
            <Stack gap={0}>
              <T weight="medium">The draft was already used</T>
              <T size="sm" color="muted">
                Successfully processed into a shipment
              </T>
            </Stack>
          </Row>

          <Row gap={3}>
            <Icon
              icon={WarningIcon}
              size="xl"
              weight="fill"
              color="text-gold"
            />
            <Stack gap={0}>
              <T weight="medium">The link is incorrect</T>
              <T size="sm" color="secondary">
                Check if you have the right URL or if the draft was deleted
              </T>
            </Stack>
          </Row>
        </Stack>

        <Button primary size="large" onClick={() => navigate("/book/details")}>
          Book a new shipment
        </Button>
      </Stack>
    );
  }

  if (error !== undefined) {
    return <LoadFailed onRetry={fetchShipmentDraft} />;
  }

  return (
    <BookShipmentDetailsScreen
      title="Finalize Your Shipment"
      description="Complete your draft and get competitive quotes from dozens of trusted carriers."
      defaultForceValidation={true}
      savedLocations={savedLocations}
      savedCommodities={savedCommodities}
      defaultPickupLocationId={defaultPickupLocationId}
      defaultDeliveryLocationId={defaultDeliveryLocationId}
    />
  );
}
